import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import type { ColumnsType } from 'antd/es/table';
import {
    Alert as ServaAlert,
    Button,
    Checkbox,
    Col,
    Input,
    Row,
    Skeleton,
    Space,
    Spin,
    Table,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import {
    buildLNKErrorMessage,
    filterLinkedAdminRows,
    updateLinkedAdminRow,
} from '../Shared/LNKHelper';
import { invalidateReportMenu } from '../Shared/ReportMenuQuery';
import Administration from '../../Constants/Endpoints/Administration';
import AddDelUserAdminReportRequest from '../../Interfaces/Requests/Administration/AddDelUserAdminReportRequest';
import GetUserAdminReportModelsUsingUserIDRequest from '../../Interfaces/Requests/Administration/GetUserAdminReportModelsUsingUserIDRequest';
import GetUserAdminReportModelsUsingUserIDResponse from '../../Interfaces/Responses/Administration/GetUserAdminReportModelsUsingUserIDResponse';

type UserReportProps = {
    userId: number;
    userName: string;
    close: () => void;
};

const UserReports: React.FC<UserReportProps> = ({ ...props }) => {
    const queryClient = useQueryClient();

    const [reports, setReports] = useState<GetUserAdminReportModelsUsingUserIDResponse[]>([]);
    const [hideUnlinkedReports, setHideUnlinkedReports] = useState(false);
    const [searchText, setSearchText] = useState('');

    const [currentError, setCurrentError] = useState('');

    const { isLoading: isGetUserReportsLoading, isError: isGetUserReportsError, error: getUserReportsError, refetch: refetchUserReports } = useQuery(
        ['user-admin-reports', props.userId],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_USER_ADMIN_REPORT_MODELS_USING_USER_ID}`;
                const requestData: GetUserAdminReportModelsUsingUserIDRequest = {
                    gur_userid: props.userId,
                };
                return await AxiosUtil.get<GetUserAdminReportModelsUsingUserIDResponse[], GetUserAdminReportModelsUsingUserIDRequest>(url, requestData);
            } catch (err) {
                setCurrentError('GET_USER_ADMIN_REPORT_MODELS_USING_USER_ID: [Get User Reports] Internal Error: ' + err);
                console.error('GET_USER_ADMIN_REPORT_MODELS_USING_USER_ID [Get User Reports] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            enabled: props.userId > 0,
            onError: (err: unknown) => {
                setCurrentError('GET_USER_ADMIN_REPORT_MODELS_USING_USER_ID: [Get User Reports] Internal Error: ' + err);
                console.error('GET_USER_ADMIN_REPORT_MODELS_USING_USER_ID [Get User Reports] Internal Error: ', err);
            },
            onSuccess: (response: GetUserAdminReportModelsUsingUserIDResponse[]) => {
                if (response) {
                    setReports(response);
                }
            },
        }
    );

    const { mutate: addRemoveReport, isLoading: isAddRemoveReportLoading, isError: isAddRemoveReportError, error: getAddRemoveReportError } = useMutation(
        async ({ add, report, }: {
            add: boolean;
            report: GetUserAdminReportModelsUsingUserIDResponse;
        }) => {
            let responseData = 0;
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.ADD_DEL_USER_ADMIN_REPORTS}`;
                const addDelUserAdminReports: AddDelUserAdminReportRequest = {
                    id: report.id,
                    reportName: report.reportName,
                    reportGroupName: report.reportGroupName,
                    userId: props.userId,
                    userName: props.userName,
                    isAdd: add,
                    isDel: !add,
                };
                const requestData: AddDelUserAdminReportRequest[] = [ addDelUserAdminReports, ];
                responseData = await AxiosUtil.post<number, AddDelUserAdminReportRequest[]>(url, requestData);
            } catch (err) {
                setCurrentError('ADD_DEL_USER_ADMIN_REPORTS: [Add/Remove User Reports] Internal Error: ' + err);
                console.error('ADD_DEL_USER_ADMIN_REPORTS [Add/Remove User Reports] Internal Error: ', err);
                return Promise.reject(err);
            }
            return { response: responseData, add, report };
        },
        {
            onError: (err: unknown) => {
                setCurrentError('ADD_DEL_USER_ADMIN_REPORTS: [Add/Remove User Reports] Internal Error: ' + err);
                console.error('ADD_DEL_USER_ADMIN_REPORTS [Add/Remove User Reports] Internal Error: ', err);
            },
            onSuccess: ({ response, add, report }) => {
                if (response) {
                    setReports(updateLinkedAdminRow(reports, report.id, add));
                    void refetchUserReports();
                    invalidateReportMenu(queryClient);
                }
            },
        }
    );

    const handleCheckboxChange = (checked: boolean, report: GetUserAdminReportModelsUsingUserIDResponse) => {
        const params = { add: checked, report: report, };
        addRemoveReport(params);
    };

    const columns: ColumnsType<GetUserAdminReportModelsUsingUserIDResponse> = [
        {
            title: 'Linked',
            key: 'isLinked',
            render: (text: unknown, record: GetUserAdminReportModelsUsingUserIDResponse) => (
                <>
                    <Checkbox
                        checked={record.isLinked}
                        onChange={(e) => handleCheckboxChange(e.target.checked, record)}
                    ></Checkbox>
                </>
            ),
        },
        { title: 'Report Group Name', dataIndex: 'reportGroupName', key: 'reportGroupName' },
        { title: 'Report Name', dataIndex: 'reportName', key: 'reportName' },
    ];

    const visibleReports = filterLinkedAdminRows(
        reports,
        hideUnlinkedReports,
        searchText.trim(),
        (report, normalizedSearchText) => normalizedSearchText === ''
            || report.reportGroupName.toLowerCase().includes(normalizedSearchText)
            || report.reportName.toLowerCase().includes(normalizedSearchText)
    );

    const handleShowLinkedReports = () => {
        setHideUnlinkedReports(!hideUnlinkedReports);
    };

    const handleSearchTextChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(aValue.target.value);
    };

    const doSearch = (sValue: string) => {
        setSearchText(sValue);
    };

    const determineWhichError = () => {
        return buildLNKErrorMessage(
            currentError,
            isGetUserReportsError,
            getUserReportsError,
            isAddRemoveReportError,
            getAddRemoveReportError
        );
    };

    return (
        <>
            <Spin spinning={isGetUserReportsLoading || isAddRemoveReportLoading}>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '50%' }}>
                {
                    (
                        isGetUserReportsError ||
                        isAddRemoveReportError
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type='error'
                        ></ServaAlert>
                    )
                }
                </div>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage User Links To Reports</Title>
                    </Text>
                </Space>
                <div style={{ width: '100%', overflowX: 'auto' }}>
                    <Row gutter={16}>
                        <Col span={4}>
                            <Button
                                onClick={handleShowLinkedReports}
                                size='small'
                                style={{
                                    background: 'none',
                                    backgroundColor: 'lightgray',
                                    color: 'blue',
                                    cursor: 'pointer',
                                }}
                                type='text'
                            >
                                {hideUnlinkedReports ? 'Show All Reports' : 'Hide Unlinked Reports'}
                            </Button>
                        </Col>
                        <Col span={4}></Col>
                        <Col span={8}>
                            <Input
                                allowClear={true}
                                value={searchText}
                                search={true}
                                style={{alignContent: 'center', width: '75%'}}
                                onChange={handleSearchTextChange}
                                onSearch={doSearch}
                            ></Input>
                        </Col>
                        <Col span={4}></Col>
                        <Col span={4}><Text type='secondary' italic ellipsis>{props.userName}</Text></Col>
                    </Row>
                    <div style={{ padding: '4px' }} />
                    {isGetUserReportsLoading && reports.length === 0 ? (
                        <Skeleton />
                    ) : (
                        <Table rowKey='id' columns={columns} dataSource={visibleReports} />
                    )}
                </div>
            </Spin>
        </>
    );
};

export default UserReports;
